/**
 * The top band of the dashboard: the section's bar, and the account cluster
 * pinned beside it.
 *
 * One definition because three things have to agree — where the bar sits,
 * where the account cluster sits, and where the page beneath them starts. Two
 * of those numbers used to live in separate files, which is how every page
 * came to clear a bar that was no longer that tall.
 */

/** Distance from the top of the window to the top of the account cluster. */
export const CHROME_TOP = { xs: 12, md: 16 };

/**
 * The bar's own height.
 *
 * It also sets the space beneath the account cluster, which is the part worth
 * knowing: the page's panel starts where this band ends and runs the full
 * width, so it passes directly under the cluster, and the only thing between
 * them is what the band has spare around the shorter pill.
 *
 * The cluster is aligned to the TOP of the band rather than centred in it, so
 * that spare room all falls below the pill instead of being split above and
 * below it. Centred, buying 8px underneath cost 16px of bar; top-aligned the
 * clearance is simply BAR_HEIGHT - PILL_HEIGHT, and the bar stays the height
 * a title and a line of explanation actually need.
 */
export const BAR_HEIGHT = 76;

/**
 * The account pill: a 40px avatar with 10px of padding above and below.
 * Here because the clearance below it is measured from this, and because
 * NavbarLinksAdmin and this file have to agree about it.
 */
export const PILL_HEIGHT = 60;

/**
 * Space between the bottom of the account pill and the top of the panel,
 * from md up — where the two share a row.
 */
export const ACCOUNT_CLEARANCE = BAR_HEIGHT - PILL_HEIGHT;

/**
 * Where the bar's own surface starts.
 *
 * On a phone it starts BELOW the cluster rather than beside it. Sharing the
 * row there costs 168 of a 390px screen — the pill, the menu button and the
 * gaps — and leaves the section's name and its Publish button about 190px to
 * live in, which is not enough for either. So on a phone the account gets its
 * own line and the bar gets the whole width.
 *
 * It costs 72px of a phone's height, which is the trade: a full-width row you
 * can read against a shared row you cannot.
 */
export const BAR_TOP = {
  xs: CHROME_TOP.xs + PILL_HEIGHT + 12,
  md: CHROME_TOP.md,
};

/**
 * Width kept clear at the end of the bar for the account cluster, including
 * the gap between them — they are separate things and should not look joined.
 *
 * Derived rather than tuned. It was a pair of hand-measured numbers until
 * adding one icon to the cluster made the pill wider than the space kept for
 * it, and the account cluster sat on top of the Publish button. A measured
 * constant is only correct for the cluster it was measured against, and
 * nothing tells you when that stops being true except a screenshot.
 *
 * The two edges do not start from the same place, which is the part that is
 * easy to get wrong: the cluster is fixed to the window and inset by
 * NAVBAR_INSET, while the bar ends where the content column ends, inset by
 * CONTENT_INSET. So the reserve has to pay back the difference between them.
 */

/** An icon button — 20px glyph, 8px padding each side, 4px margin each side. */
const ICON_SLOT = 44;
/** The pill's own padding, both ends. */
const PILL_PADDING = 20;
/** Distance from the window's right edge to the cluster. */
const NAVBAR_INSET = { md: 30, xl: 30 };
/** Distance from the window's right edge to the end of the content column. */
const CONTENT_INSET = { md: 24, xl: 24 };
/** Visible space between the bar and the cluster, horizontally. */
const GAP = { md: 14, xl: 14 };
/**
 * What is in the pill. The light/dark switch is the third one, and only from
 * md up — on a phone that row has no 44px to spare, so the switch moves into
 * the account menu instead. Keep this in step with NavbarLinksAdmin.
 */
const CONTROLS = { md: 3, xl: 3 };
/**
 * The menu button that opens the sidebar as a drawer, plus the gap after it.
 * It sits to the LEFT of the pill and shows at every width below xl, where
 * the sidebar is permanent and it disappears — so it is part of the cluster
 * on a laptop and not on a desktop.
 *
 * It was missing from this sum entirely, which put it 16px on top of the bar
 * on every window narrower than 1536.
 */
const MENU_BUTTON = { md: 40 + 10 + 8, xl: 0 };

const reserve = (bp) => NAVBAR_INSET[bp] + PILL_PADDING + (CONTROLS[bp] * ICON_SLOT)
  + MENU_BUTTON[bp] - CONTENT_INSET[bp] + GAP[bp];

/**
 * Width kept clear at the end of the bar for the account cluster.
 *
 * Zero on a phone: there the cluster is on its own line above (see BAR_TOP)
 * and the bar has the row to itself.
 */
export const ACCOUNT_RESERVE = { xs: 0, md: reserve('md'), xl: reserve('xl') };

/**
 * Space between the bar and the panel beneath it.
 *
 * They used to share an edge — the bar was square along its bottom and the
 * panel square along its top — which made one tall shape with a seam across
 * it rather than a title above a page. Separate things, separate surfaces.
 *
 * Owned by the bar (it applies this as its own bottom padding) rather than by
 * the panel: the bar is the sticky element, so its background is what the
 * page scrolls behind. A margin on the panel would leave a transparent stripe
 * for content to show through as it passed.
 */
export const BAR_GAP = '14px';

/**
 * Extra offset a page adds above its panel.
 *
 * Zero: the gap now lives under the bar (see BAR_GAP), so a page adding its
 * own would double it. It stays a named constant because every page imports
 * it, and somewhere will eventually want one.
 */
export const CONTENT_TOP = 0;

const chromeGeometry = {
  CHROME_TOP, BAR_TOP, BAR_HEIGHT, PILL_HEIGHT,
  ACCOUNT_CLEARANCE, ACCOUNT_RESERVE, CONTENT_TOP, BAR_GAP,
};
export default chromeGeometry;
